import React, { useContext, useEffect, useState } from 'react';
import { EmeraldDataTable } from '@emerald-react/data-table';
import { EmeraldModal } from '@emerald-react/modal';
import { EmeraldButton } from '@emerald-react/button';
import { EmeraldInput } from '@emerald-react/input';
import { EmeraldIconButton } from '@emerald-react/icon-button';
import {
  EmeraldProgressBar,
  EmeraldProgressSpinner,
} from '@emerald-react/progress-indicator';
import { EmeraldBanner } from '@emerald-react/banner';
import { useApi } from '@backstage/core-plugin-api';
import { EmeraldIcon } from '@emerald-react/icon';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import cx from 'classnames';
import DesignToCodeLayout from '../layout/layout';
import './../layout/style.css';
import { AuthContext } from '../../../providers/AuthProvider';
import {
  DesignToCodeApi,
  designToCodeRef,
} from '../../../apis/designToCodeConfigApi';
import { FrontendConfigContext } from '../../../providers/FrontendConfigProvider';

type Document = {
  createdAt: string;
  designTitle: string;
  id: string;
  lastUpdatedAt: string;
  s3Key: string;
  sizeBytes: number;
  userEmail: string;
  userName: string;
};

const getDocuments = (searchQuery: string, documentsData: any) => {
  let data: Document[] = [];

  documentsData?.documents?.length > 0 &&
    documentsData?.documents?.map((doc: any) => {
      const createdAtEST = doc.createdAt
        ? DateTime.fromISO(doc.createdAt).toFormat('dd MMM yyyy, hh:mm a')
        : '-';
      const lastUpdatedAtEST = doc.lastUpdatedAt
        ? DateTime.fromISO(doc.lastUpdatedAt).toFormat('dd MMM yyyy, hh:mm a')
        : '-';

      data.push({
        createdAt: createdAtEST,
        designTitle: doc.metadata.designTitle || 'Sample Design',
        id: doc.id,
        lastUpdatedAt: lastUpdatedAtEST,
        s3Key: doc.s3Key,
        sizeBytes: doc.sizeBytes,
        userEmail: doc.userEmail,
        userName: doc.userName,
      });
    });

  return data.filter(
    (doc: Document) =>
      doc.designTitle.toLowerCase().includes(searchQuery) ||
      doc.userName.toLowerCase().includes(searchQuery) ||
      doc.userEmail.toLowerCase().includes(searchQuery),
  );
};

const getLink = (rowIndex: number, columnIndex: number, cellObj: any) => {
  const id = `/design-to-code/${cellObj.rowObj.id}`;
  return (
    <Link to={id} title={cellObj.rowObj.designTitle} className="eds-table-link">
      {cellObj.rowObj.designTitle}
    </Link>
  );
};

const DesignToCodeDashboard = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [documentsData, setDocumentsData] = useState<any>(null);
  const { profEmail, profileName, isAdmin } = useContext(AuthContext);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showModalMessage, setShowModalMessage] = useState('');
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const designToCode: DesignToCodeApi = useApi(designToCodeRef);
  const { frontendConfigData } = useContext(FrontendConfigContext);
  const [uniqueId, setUniqueId] = useState<string | null>(null);
  const [userInfoStatus, setUserInfoStatus] = useState<boolean | null>(null);
  const [isUserInfoLoading, setIsUserInfoLoading] = useState(false);
  const [initiateFetchDocumentData, setInitiateFetchDocumentData] =
    useState(false);
  const [showFigmaImportModal, setShowFigmaImportModal] =
    useState<boolean>(false);
  const [figmaImportUrl, setFigmaImportUrl] = useState<string>('');
  const isAdminTest = false;

  useEffect(() => {
    if (initiateFetchDocumentData) {
      setInitiateFetchDocumentData(false);
      fetchDocumentsData();
    }
  }, [initiateFetchDocumentData]);

  useEffect(() => {
    if (frontendConfigData && designToCode) {
      designToCode.setAuthorizationKey(frontendConfigData?.figma?.token);
      designToCode.setFigmaApiTokenAndURL(
        frontendConfigData?.figma?.apiToken,
        frontendConfigData?.figma?.apiBaseUrl,
      );
      if (window.location.hash) {
        setUniqueId(window.location.hash.split('#')[1]);
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search,
        );
      }
      if (profEmail && profileName) {
        setInitiateFetchDocumentData(true);
      }
    }
  }, [frontendConfigData, designToCode, profEmail, profileName, isAdmin]);

  useEffect(() => {
    if (uniqueId && userInfoStatus === null && !isUserInfoLoading) {
      setIsUserInfoLoading(true);
      updateUserInfo(uniqueId);
    }
  }, [uniqueId, userInfoStatus]);

  const updateUserInfo = (uniqueId: string) => {
    designToCode
      .insertUserIfo(profEmail?.toLowerCase(), profileName, null, uniqueId)
      .then((result: any) => {
        //console.log('User info inserted successfully: ', result);
        setUserInfoStatus(result.status === 'success');
      })
      .catch((error: any) => {
        //console.error('Error inserting user info: ', error);
        setUserInfoStatus(false);
      })
      .finally(() => {
        setIsUserInfoLoading(false);
        setInitiateFetchDocumentData(true);
      });
  };
  const fetchDocumentsData = () => {
    setLoading(true);
    (isAdmin || isAdminTest
      ? designToCode.fetchDocumentsAdminMode(
          profEmail?.toLowerCase(),
          profileName,
          null,
        )
      : designToCode.fetchDocuments(profEmail?.toLowerCase(), profileName, null)
    )
      .then((result: any) => {
        setDocumentsData(result);
      })
      .catch((error: any) => {
        setDocumentsData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteDocument = () => {
    if (selectedRow?.rowObj?.id) {
      setLoading(true);
      (isAdmin || isAdminTest
        ? designToCode.deleteDocumentAdminMode(
            profEmail?.toLowerCase(),
            profileName,
            null,
            selectedRow.rowObj.id,
          )
        : designToCode.deleteDocument(
            profEmail?.toLowerCase(),
            profileName,
            null,
            selectedRow.rowObj.id,
          )
      )
        .then((result: any) => {
          if (result?.status === 'success') {
            setInitiateFetchDocumentData(true);
            setShowModalMessage(
              result.message || 'Document deleted successfully',
            );
            setShowSuccessModal(true);
          } else {
            setLoading(false);
            setShowErrorModal(true);
            setShowModalMessage(result.message || 'Something went wrong');
          }
        })
        .catch((error: any) => {
          setLoading(false);
          setShowErrorModal(true);
          setShowModalMessage(error?.message || 'Something went wrong');
        })
        .finally(() => {
          setSelectedRow(null);
        });
    }
  };

  const onDeleteDocumentSelect = (data: any) => {
    setSelectedRow(data);
    setShowModalMessage('Do you want to delete?');
    setShowConfirmModal(true);
  };
  const modelConfirmationAction = () => {
    setShowConfirmModal(false);
    setShowSuccessModal(false);
    setShowErrorModal(false);
    handleDeleteDocument();
  };
  const modelCancelAction = () => {
    setShowConfirmModal(false);
    setShowSuccessModal(false);
    setShowErrorModal(false);
    setSelectedRow(null);
  };

  const actionButtons = () => {
    return showConfirmModal ? (
      <>
        <div className="mdc-dialog__button">
          <EmeraldButton
            label="Cancel"
            onClick={modelCancelAction}
            type="Secondary"
          />
        </div>
        <div className="mdc-dialog__button">
          <EmeraldButton label="Yes" onClick={modelConfirmationAction} />
        </div>
      </>
    ) : (
      <div className="mdc-dialog__button">
        <EmeraldButton label="Ok" onClick={modelCancelAction} />
      </div>
    );
  };
  const customCell = (value: string) => {
    return <span title={value}>{value}</span>;
  };

  const getJSONFromFigma = () => {
    const url = new URL(figmaImportUrl);

    // Get the pathname (everything after the host, before the query string)
    const path = url.pathname.split('/');

    // Get query parameters as an object
    const queryParams: any = {};
    for (const [key, value] of url.searchParams.entries()) {
      queryParams[key] = value;
    }
    if (
      path.length < 4 ||
      !url ||
      queryParams['node-id'] === undefined ||
      queryParams['p']
    ) {
      alert('Please provide a valid Figma URL with a design selected.');
      return;
    }
    setShowFigmaImportModal(false);
    setLoading(true);
    setFigmaImportUrl('');
    const fileKey = path[2];
    const nodeId = queryParams['node-id'];
    designToCode
      .importFigmaDesign(fileKey, nodeId)
      .then(async (result: any) => {
        result.fileKey = fileKey;
        console.log('Figma API response:', result);
        designToCode
          .uploadDocument(profEmail?.toLowerCase(), profileName, null, {
            content: result,
            metadata: {
              designTitle:
                result?.nodes[nodeId.replace(/-/g, ':')]?.document?.name ||
                'Figma Design',
            },
          })
          .then((finalResult: any) => {
            setInitiateFetchDocumentData(true);
          })
          .catch((error: any) => {
            alert(`Error uploading document: ${error.message}`);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch((error: any) => {
        alert(`Error fetching Figma data: ${error.message}`);
        setLoading(false);
      });
    // .finally(() => {
    //   setLoading(false);
    // });
  };

  const actionButtonsFigmaImport = () => {
    return (
      <>
        <div className="mdc-dialog__button">
          <EmeraldButton
            label="Cancel"
            onClick={() => {
              setShowFigmaImportModal(false);
              setFigmaImportUrl('');
            }}
            type="Secondary"
          />
        </div>
        <div className="mdc-dialog__button">
          <EmeraldButton label="Yes" onClick={() => getJSONFromFigma()} />
        </div>
      </>
    );
  };

  const getColumnDefs = (): any[] => [
    {
      field: 'designTitle',
      headerName: 'Design Title',
      cellTemplate: (rowIdx: number, idx: number, data: any) =>
        getLink(rowIdx, idx, data),
    },
    {
      field: 'createdAt',
      headerName: 'Scanned on',
      cellTemplate: (rowIdx: number, idx: number, data: any) =>
        customCell(data?.rowObj?.createdAt),
    },
    {
      field: 'lastUpdatedAt',
      headerName: 'Updated on',
      cellTemplate: (rowIdx: number, idx: number, data: any) =>
        customCell(data?.rowObj?.lastUpdatedAt),
    },
    {
      field: 'userName',
      headerName: 'User name',
      cellTemplate: (rowIdx: number, idx: number, data: any) =>
        customCell(data?.rowObj?.userName),
    },
    {
      field: 'userEmail',
      headerName: 'User email',
      cellTemplate: (rowIdx: number, idx: number, data: any) =>
        customCell(data?.rowObj?.userEmail),
    },
    {
      field: 'sizeBytes',
      headerName: 'Size',
      cellTemplate: (rowIdx: number, idx: number, data: any) =>
        customCell(
          (+data?.rowObj?.sizeBytes / (1024 * 1024)).toFixed(2) + ' MB',
        ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      cellTemplate: (rowIdx: number, idx: number, data: any) => (
        <section>
          <EmeraldIcon
            onClick={() => onDeleteDocumentSelect(data)}
            icon="delete"
            type="rounded"
          />
        </section>
      ),
    },
  ];

  return (
    <DesignToCodeLayout>
      <div
        className={cx('design-to-code-page-container figma-import-option', {
          'hide-pagination': !getDocuments(searchQuery, documentsData)?.length,
        })}
      >
        {loading && <EmeraldProgressBar determinate={false} />}
        <EmeraldIconButton
          className="import-figma-design"
          icon="upload"
          type="Icon"
          onClick={() => setShowFigmaImportModal(true)}
        />
        <EmeraldDataTable
          headerParams={{
            title: 'Figma imports',
            enabled: true,
            searchParams: {
              enabled: true,
              placeholder: 'Search',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange: (event: any, value: string) => {
                event.preventDefault();
                event.stopPropagation();
                setSearchQuery(value);
              },
            },
          }}
          columnDefs={getColumnDefs()}
          paginationParams={{
            enabled: true,
            pageSize: 10,
            total: getDocuments(searchQuery, documentsData).length,
            currentPage: 1,
            pageSizeOptions: [5, 10, 20],
          }}
          rowData={getDocuments(searchQuery, documentsData)}
          allowResizeColumns={false}
        />

        <EmeraldModal
          opened={showConfirmModal || showSuccessModal || showErrorModal}
          actionTemplate={actionButtons()}
          headerText={showModalMessage}
        ></EmeraldModal>

        {uniqueId && (
          <EmeraldModal
            headerText="SSO Connection Status"
            opened={true}
            widthInRem={25}
            heightInRem={17.5}
          >
            {userInfoStatus === null && (
              <EmeraldProgressSpinner
                label="Establishing SSO connection..."
                determinate={false}
              />
            )}
            {userInfoStatus === true && (
              <div className="userinfo-success-message">
                <EmeraldBanner
                  content="SSO connection established and user authenticated successfully."
                  open
                  variant="success"
                />
                <div className="userinfo-action-buttons">
                  <EmeraldButton
                    label="Close"
                    onClick={() => {
                      setUniqueId(null);
                    }}
                  />
                </div>
              </div>
            )}
            {userInfoStatus === false && (
              <div className="userinfo-success-message">
                <EmeraldBanner
                  content="SSO connection failed. Please try again."
                  open
                  variant="error"
                />
                <div className="userinfo-action-buttons">
                  <EmeraldButton
                    label="Close"
                    onClick={() => {
                      setUniqueId(null);
                    }}
                  />
                </div>
              </div>
            )}
          </EmeraldModal>
        )}

        {showFigmaImportModal && (
          <EmeraldModal
            opened={true}
            actionTemplate={actionButtonsFigmaImport()}
            headerText="Import Figma Design"
            widthInRem={40}
          >
            <EmeraldInput
              value={figmaImportUrl}
              onChange={(event, changedValue) =>
                setFigmaImportUrl(changedValue)
              }
            />
          </EmeraldModal>
        )}
      </div>
    </DesignToCodeLayout>
  );
};

export default DesignToCodeDashboard;
